import { isClerkAPIResponseError } from "@clerk/react/errors";
import { useSignIn, useSignUp } from "@clerk/react/legacy";
import { useEffect, useState } from "react";

import { AuthScreen } from "@/components/auth/auth-screen";
import { InlineError } from "@/components/auth/inline-error";
import { PasswordField } from "@/components/auth/password-field";
import { PrimaryButton } from "@/components/auth/primary-button";
import { TextField } from "@/components/auth/text-field";
import { redirectAfterAuth } from "@/lib/auth-helpers";
import {
  clerkUsernameFromEmail,
  parseInvitationTicket,
} from "@/lib/clerk-username";

const clerkMessage = (error: unknown, fallback: string): string => {
  if (isClerkAPIResponseError(error)) {
    return error.errors[0]?.longMessage ?? error.errors[0]?.message ?? fallback;
  }
  return fallback;
};

const AcceptInvitationPage = () => {
  const ticket = parseInvitationTicket(window.location.search);
  const { isLoaded: signInLoaded, setActive, signIn } = useSignIn();
  const { isLoaded: signUpLoaded, signUp } = useSignUp();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (
      !(
        ticket.token &&
        ticket.status === "sign_in" &&
        signInLoaded &&
        signIn &&
        setActive
      )
    ) {
      return;
    }
    const token = ticket.token;
    let cancelled = false;
    const redeem = async () => {
      setPending(true);
      try {
        const attempt = await signIn.create({
          strategy: "ticket",
          ticket: token,
        });
        if (attempt.status === "complete" && attempt.createdSessionId) {
          await setActive({ session: attempt.createdSessionId });
          window.location.assign(redirectAfterAuth());
        }
      } catch (err) {
        if (!cancelled) {
          setError(clerkMessage(err, "This invitation could not be accepted."));
        }
      } finally {
        if (!cancelled) {
          setPending(false);
        }
      }
    };
    redeem().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [setActive, signIn, signInLoaded, ticket.status, ticket.token]);

  const onSignUp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!(signUpLoaded && signUp && setActive && ticket.token)) {
      return;
    }
    if (password !== confirm) {
      setError("Passwords don’t match.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      const attempt = await signUp.create({
        firstName,
        lastName,
        password,
        strategy: "ticket",
        ticket: ticket.token,
        username: clerkUsernameFromEmail("customer@anomalos.com"),
      });
      if (attempt.status === "complete" && attempt.createdSessionId) {
        await setActive({ session: attempt.createdSessionId });
        window.location.assign(redirectAfterAuth());
        return;
      }
      setError("Finish the remaining steps, then continue to Studio.");
    } catch (err) {
      setError(clerkMessage(err, "This invitation could not be accepted."));
    } finally {
      setPending(false);
    }
  };

  if (!ticket.token) {
    return (
      <AuthScreen
        subtitle="Open this invitation from the email we sent you, then continue to Studio."
        title="Invitation"
      />
    );
  }

  if (ticket.status === "sign_up") {
    return (
      <AuthScreen
        subtitle="Create your password to join the workspace."
        title="Accept invitation"
      >
        <form className="flex flex-col gap-5" onSubmit={onSignUp}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              id="invite-first"
              label="First name"
              onChange={(event) => setFirstName(event.target.value)}
              value={firstName}
            />
            <TextField
              id="invite-last"
              label="Last name"
              onChange={(event) => setLastName(event.target.value)}
              value={lastName}
            />
          </div>
          <PasswordField
            autoComplete="new-password"
            id="invite-password"
            label="Password"
            onChange={setPassword}
            value={password}
          />
          <PasswordField
            autoComplete="new-password"
            id="invite-confirm"
            label="Confirm password"
            onChange={setConfirm}
            value={confirm}
          />
          <InlineError message={error} />
          <PrimaryButton busy={pending} disabled={!signUpLoaded} type="submit">
            Continue
          </PrimaryButton>
        </form>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      subtitle={pending ? "Signing you in…" : "Continue to Studio."}
      title="Accept invitation"
    >
      <InlineError message={error} />
    </AuthScreen>
  );
};

export default AcceptInvitationPage;
